import { FormEvent, useEffect, useState } from 'react';
import { Divider, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { useAuth0 } from '../../../auth/AuthProvider';
import { formatInputDate } from '../../../common/utilities/dateFormatter';
import { displayEmpty } from '../../../common/utilities/displayEmpty';
import GenderOptionLabel from '../../../components/Form/GenderOptionLabel';
import { GENDER_OPTIONS } from '../../../constants/gender';
import useGetUser from '../../../hooks/useGetUser';
import useUpdateUser from '../../../hooks/useUpdateUser';
import {
  isBasicPersonalInfoFieldRequired,
  isProfileFieldFilled,
} from '../utilities/profileCompleteness';
import ProfileRequiredBadge from '../components/ProfileRequiredBadge';
import ProfileSubpageState from '../components/ProfileSubpageState';

type BasicInfoFormState = {
  fullName: string;
  gender: string;
  phone: string;
  dateOfBirth: string;
  specialRequest: string;
};

const emptyFormState: BasicInfoFormState = {
  fullName: '',
  gender: '',
  phone: '',
  dateOfBirth: '',
  specialRequest: '',
};

const BasicPersonalInfo = () => {
  const { user } = useAuth0();
  const updateUser = useUpdateUser();

  const {
    user: userData,
    isLoading: userDataLoading,
    isError,
    refetch,
    isFetching,
  } = useGetUser(user?.sub ?? '', true);

  const [editMode, setEditMode] = useState(false);
  const [formState, setFormState] =
    useState<BasicInfoFormState>(emptyFormState);

  const syncFromServer = () => {
    setFormState({
      fullName: userData?.fullName ?? '',
      gender: userData?.gender ?? '',
      phone: userData?.phone ?? '',
      dateOfBirth: userData?.dateOfBirth ?? '',
      specialRequest: userData?.specialRequest ?? '',
    });
  };

  useEffect(() => {
    if (!editMode) {
      syncFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when server data or edit mode changes
  }, [userData, editMode]);

  if (userDataLoading || isError) {
    return (
      <ProfileSubpageState
        title="Basic personal info"
        isLoading={userDataLoading}
        isError={isError}
        errorMessage="We couldn’t load your personal details. Please try again."
        onRetry={() => {
          void refetch();
        }}
        isRetrying={isFetching && isError}
      />
    );
  }

  const handleCancel = () => {
    syncFromServer();
    setEditMode(false);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleGenderChange = (event: SelectChangeEvent) => {
    setFormState(prev => ({ ...prev, gender: event.target.value }));
  };

  const handlePhoneChange = (value: string) => {
    setFormState(prev => ({ ...prev, phone: value }));
  };

  const handleFormSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!userData) {
      return;
    }

    try {
      await updateUser.mutateAsync({
        ...userData,
        fullName: formState.fullName,
        gender: formState.gender,
        phone: formState.phone,
        dateOfBirth: formState.dateOfBirth,
        specialRequest: formState.specialRequest,
      });
      setEditMode(false);
    } catch {
      // Keep edit mode open; toast handled by mutation feedback.
    }
  };

  const isSectionRequired =
    (isBasicPersonalInfoFieldRequired('fullName') &&
      !isProfileFieldFilled(formState.fullName)) ||
    (isBasicPersonalInfoFieldRequired('gender') &&
      !isProfileFieldFilled(formState.gender)) ||
    (isBasicPersonalInfoFieldRequired('phone') &&
      !isProfileFieldFilled(formState.phone));

  const email = userData?.email ?? '';

  return (
    <div className="px-6 md:px-0 md:min-w-[772px]">
      <div className="flex flex-col">
        <h1 className="text-2xl md:text-3xl font-semibold text-neutral-800">
          Basic personal info
        </h1>
        <p className="mt-2 text-base text-neutral-500">
          Provide basic personal details to inform the boat organizer
        </p>
      </div>
      <form className="mt-6" onSubmit={handleFormSubmit}>
        <div className="flex justify-between font-semibold">
          <div className="flex items-center">
            <span className="text-xl">Personal details</span>
            {editMode && isSectionRequired && <ProfileRequiredBadge />}
          </div>
          <button
            type="button"
            className="underline"
            onClick={() => (editMode ? handleCancel() : setEditMode(true))}
          >
            {editMode ? 'Cancel' : 'Edit'}
          </button>
        </div>

        <div className="flex flex-col mt-4">
          <span className="text-neutral-500">Full name</span>
          {editMode ? (
            <input
              type="text"
              name="fullName"
              value={formState.fullName}
              onChange={handleInputChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
              aria-label="Full name"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(formState.fullName)}
            </span>
          )}
        </div>
        <Divider />

        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Gender</span>
          {editMode ? (
            <div className="mb-4 mt-4">
              <Select
                value={formState.gender}
                onChange={handleGenderChange}
                displayEmpty
                fullWidth
                aria-label="Gender"
                renderValue={selected =>
                  selected ? (
                    <GenderOptionLabel gender={selected} />
                  ) : (
                    <em>Select gender</em>
                  )
                }
              >
                <MenuItem value="">
                  <em>Select gender</em>
                </MenuItem>
                {formState.gender &&
                  !(GENDER_OPTIONS as readonly string[]).includes(
                    formState.gender,
                  ) && (
                    <MenuItem value={formState.gender}>
                      <GenderOptionLabel gender={formState.gender} />
                    </MenuItem>
                  )}
                {GENDER_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>
                    <GenderOptionLabel gender={option} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {formState.gender ? (
                <GenderOptionLabel gender={formState.gender} />
              ) : (
                displayEmpty(formState.gender)
              )}
            </span>
          )}
        </div>
        <Divider />

        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Email</span>
          <span className="mt-2 mb-1 text-neutral-800">
            {displayEmpty(email)}
          </span>
          <p className="mb-4 text-sm text-neutral-500">
            Email is tied to your login and can’t be changed here.
          </p>
        </div>
        <Divider />

        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Phone number</span>
          {editMode ? (
            <div className="mb-4 mt-4">
              <MuiTelInput
                fullWidth
                value={formState.phone}
                onChange={handlePhoneChange}
                aria-label="Phone number"
              />
            </div>
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(formState.phone)}
            </span>
          )}
        </div>
        <Divider />

        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Date of birth (optional)</span>
          {editMode ? (
            <input
              type="date"
              name="dateOfBirth"
              value={formatInputDate(formState.dateOfBirth)}
              onChange={handleInputChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
              aria-label="Date of birth"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(formatInputDate(formState.dateOfBirth))}
            </span>
          )}
        </div>
        <Divider />

        <div className="mt-4 flex flex-col mb-8">
          <span className="text-neutral-500">Special requests (optional)</span>
          {editMode ? (
            <textarea
              name="specialRequest"
              value={formState.specialRequest}
              onChange={handleInputChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
              aria-label="Special requests"
              rows={4}
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800 whitespace-pre-wrap">
              {displayEmpty(formState.specialRequest)}
            </span>
          )}
        </div>

        {editMode && (
          <button
            type="submit"
            disabled={updateUser.isPending}
            className="mt-4 mb-24 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {updateUser.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
    </div>
  );
};

export default BasicPersonalInfo;
